// Interactive reveal answer module
// Converts {{interactive-reveal-answer: Title}} ... {{/interactive-reveal-answer}}
// Answers are visible by default; click to reveal correct/wrong state.

import { processTooltipReferences } from './footnotes.js';

/**
 * @param {Element} container
 * @param {Map}     footnotesMap
 */
export function processInteractiveRevealAnswerBlocks(container, footnotesMap) {
  const allElements = Array.from(container.children);
  const startRegex   = /\{\{interactive-reveal-answer:\s*([^}]+)\}\}/;
  const endRegex     = /\{\{\/interactive-reveal-answer\}\}/;
  const correctRegex = /\{\{correct:\s*([^}]+)\}\}/;
  const wrongRegex   = /\{\{wrong:\s*([^}]+)\}\}/;

  for (let i = 0; i < allElements.length; i++) {
    const element = allElements[i];
    const startMatch = (element.textContent || '').match(startRegex);
    if (!startMatch) continue;

    const title = startMatch[1].trim();
    let endIndex = -1, endElement = null;

    for (let j = i; j < allElements.length; j++) {
      if ((allElements[j].textContent || '').match(endRegex)) {
        endIndex = j; endElement = allElements[j]; break;
      }
    }

    if (endIndex === -1) {
      console.warn(`[Interactive Reveal Answer] "{{interactive-reveal-answer: ${title}}}" has no closing {{/interactive-reveal-answer}}.`);
      continue;
    }

    // ── Collect elements between markers ──────────────────────────────────────
    const contentElements = [];
    const elementsToRemove = [];

    const startHTML = element.innerHTML || '';
    const escapedTitle = title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const startHTMLMatch = startHTML.match(
      new RegExp(`\\{\\{interactive-reveal-answer:\\s*${escapedTitle}\\}\\}\\s*(.+)`, 's')
    );

    if (startHTMLMatch?.[1]?.trim()) {
      const tmp = document.createElement('div');
      tmp.innerHTML = startHTMLMatch[1].trim();
      Array.from(tmp.children).forEach(c => contentElements.push(c));
    }

    if (element.textContent.trim() === startMatch[0].trim()) {
      elementsToRemove.push(element);
    } else if (startHTMLMatch) {
      const updated = startHTML.replace(startHTMLMatch[0], '').trim();
      updated ? (element.innerHTML = updated) : elementsToRemove.push(element);
    }

    for (let k = i + 1; k < endIndex; k++) {
      contentElements.push(allElements[k]);
      elementsToRemove.push(allElements[k]);
    }

    const endHTML  = endElement.innerHTML || '';
    const endText  = endElement.textContent || '';
    const endHTMLMatch = endHTML.match(/(.+?)\s*\{\{\/interactive-reveal-answer\}\}/s);

    if (endHTMLMatch?.[1]?.trim()) {
      const tmp = document.createElement('div');
      tmp.innerHTML = endHTMLMatch[1].trim();
      Array.from(tmp.children).forEach(c => contentElements.push(c));
    }

    if (endText.trim() === '{{/interactive-reveal-answer}}') {
      elementsToRemove.push(endElement);
    } else if (endHTML.includes('{{/interactive-reveal-answer}}')) {
      const updated = endHTMLMatch
        ? endHTML.replace(endHTMLMatch[0], endHTMLMatch[1] || '').trim()
        : endHTML.replace(/\{\{\/interactive-reveal-answer\}\}/g, '').trim();
      updated ? (endElement.innerHTML = updated) : elementsToRemove.push(endElement);
    }

    // ── Parse answers ──────────────────────────────────────────────────────────
    const answers = [];
    contentElements.forEach(el => {
      const text = el.textContent || '';
      const correctMatch = text.match(correctRegex);
      const wrongMatch   = text.match(wrongRegex);
      const matched      = correctMatch || wrongMatch;
      if (!matched) return;

      const type       = correctMatch ? 'correct' : 'wrong';
      const answerText = matched[1].trim();

      if (text.trim() === matched[0].trim()) {
        if (!elementsToRemove.includes(el)) elementsToRemove.push(el);
      } else {
        const updated = el.innerHTML.replace(matched[0], '').trim();
        updated ? (el.innerHTML = updated) : elementsToRemove.push(el);
      }
      answers.push({ type, text: answerText });
    });

    if (!answers.length) {
      console.warn(`[Interactive Reveal Answer] "{{interactive-reveal-answer: ${title}}}" has no {{correct:}} or {{wrong:}} answers.`);
    }

    // ── Build DOM ──────────────────────────────────────────────────────────────
    const block = document.createElement('div');
    block.className = 'interactive-reveal-answer';

    const header = document.createElement('div');
    header.className = 'interactive-reveal-answer__header';

    const titleSpan = document.createElement('span');
    titleSpan.className = 'interactive-reveal-answer__title';
    titleSpan.textContent = title;
    header.appendChild(titleSpan);

    const answersOl = document.createElement('ol');
    answersOl.className = 'interactive-reveal-answer__answers';

    answers.forEach((answer, idx) => {
      const li = document.createElement('li');
      li.className = 'interactive-reveal-answer__answer';
      li.setAttribute('data-answer-type', answer.type);
      li.setAttribute('data-answer-index', idx);

      const btn = document.createElement('button');
      btn.className = 'interactive-reveal-answer__answer-button';
      btn.setAttribute('type', 'button');

      const textSpan = document.createElement('span');
      textSpan.className = 'interactive-reveal-answer__answer-text';
      textSpan.textContent = answer.text;
      btn.appendChild(textSpan);

      li.appendChild(btn);
      answersOl.appendChild(li);
    });

    block.appendChild(header);
    block.appendChild(answersOl);

    // Insert / replace
    if (elementsToRemove.includes(element)) {
      element.replaceWith(block);
    } else {
      element.insertAdjacentElement('afterend', block);
    }

    elementsToRemove.forEach(el => { if (el.parentNode && el !== element) el.remove(); });

    // Tooltip refs inside title area
    if (footnotesMap) processTooltipReferences(block, footnotesMap);

    // Click handlers
    const buttons = block.querySelectorAll('.interactive-reveal-answer__answer-button');
    const totalCorrect = answers.filter(a => a.type === 'correct').length;
    const selectedCorrect = new Set();
    let isAnswered = false;

    buttons.forEach(btn => {
      btn.addEventListener('click', () => {
        if (isAnswered) return;
        const li = btn.closest('.interactive-reveal-answer__answer');
        const type = li.getAttribute('data-answer-type');

        if (type === 'correct') {
          const idx = li.getAttribute('data-answer-index');
          if (selectedCorrect.has(idx)) return;
          selectedCorrect.add(idx);
          li.classList.add('interactive-reveal-answer__answer--correct', 'is-revealed');

          // Only reveal all when all correct answers have been selected
          if (selectedCorrect.size >= totalCorrect) {
            isAnswered = true;
            block.classList.add('is-answered');
            block.querySelectorAll('.interactive-reveal-answer__answer').forEach(item => {
              const t = item.getAttribute('data-answer-type');
              item.classList.add(`interactive-reveal-answer__answer--${t}`, 'is-revealed');
            });
          }
        } else {
          li.classList.add('interactive-reveal-answer__answer--wrong', 'is-revealed');
        }
      });
    });

    i = endIndex;
  }
}
